import { ref, computed, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import type { GenericField } from '@Types';
import type { FieldInitialSettings, FieldSchema, RawField } from '@FormModule';
import { useAnimalProfileStore } from '@Stores';
import { useForm } from '@FormModule';

const currentSchemaModels = ref<Set<GenericField>>(new Set());
const fieldsSchema = ref<FieldSchema[]>([]);
const formError = ref();
const isInitialFormSetup = ref(false);
const lastHoldReasonValue = ref(null);
const isSimplifiedEditMode = ref(false);

export default function () {
  const { pageData } = storeToRefs(useAnimalProfileStore());

  const { getDetailsSchema, wasFormSubmitted } = useForm();
  const { t } = useI18n();

  const startDateField = computed(() => fieldsSchema.value.find(({ name }) => name === 'start_date'));
  const startDateModel = computed(() => startDateField.value?.model);

  const holdStatusField = computed(() => fieldsSchema.value.find(({ name }) => name === 'hold_status'));
  const holdStatusModel = computed(() => holdStatusField.value?.model);

  const holdCommentField = computed(() => fieldsSchema.value.find(({ name }) => name === 'hold_comment'));
  const holdCommentModel = computed(() => holdCommentField.value?.model);

  const holdStatusOptions = computed(() => [
    { name: 'Select Hold Status', value: 'holdStatus' },
    { name: 'Custom Hold Status', value: 'custom' },
  ]);

  const rawFields: RawField[] = [
    {
      label: 'Hold Status',
      name: 'hold_status',
      required: true,
      type: 'radio',
      value: '',
    },
    {
      label: 'Hold Reason',
      name: 'hold_reason',
      required: true,
      type: 'select',
      value: '',
    },
    {
      label: 'Custom Hold Reason',
      name: 'custom_hold_reason',
      required: false,
      type: 'input',
      value: '',
    },
    {
      label: 'Start Date',
      name: 'start_date',
      required: true,
      type: 'date',
      value: '',
    },
    {
      label: 'End Date',
      name: 'end_date',
      required: true,
      type: 'date',
      value: '',
    },
    {
      label: 'Length of Hold',
      name: 'length_of_hold',
      required: false,
      type: 'field_string',
      value: '',
    },
    {
      label: 'Comment',
      name: 'hold_comment',
      required: false,
      type: 'textarea',
      value: '',
    },
  ];
  const fieldsSettings: FieldInitialSettings = {
    hold_status: {
      edit: {
        options: holdStatusOptions.value,
      },
    },
    hold_reason: {
      edit: {
        label: 'label',
        options: pageData.value?.holds?.values || [],
      },
    },
    start_date: {
      settings: {
        showTime: true,
      },
    },
    end_date: {
      settings: {
        showTime: true,
      },
    },
  };

  const endDateField = computed(() => fieldsSchema.value.find(({ name }) => name === 'end_date'));
  const endDateModel = computed(() => endDateField.value?.model);
  const holdReasonField = computed(() => fieldsSchema.value.find(({ name }) => name === 'hold_reason'));
  const holdReasonModel = computed(() => holdReasonField.value?.model);

  const lengthOfHold = computed(() => {
    if (!startDateModel?.value || !endDateModel?.value) return t('animals.individualPage.holdLimits.emptyLengthOfHold');

    const start = new Date(startDateModel.value);
    const end = new Date(endDateModel.value);
    const diffTime = Math.abs(end.getTime() - start.getTime());

    const totalMinutes = Math.floor(diffTime / (1000 * 60));
    const minutes = totalMinutes % 60;

    const totalHours = Math.floor(diffTime / (1000 * 60 * 60));
    const hours = totalHours % 24;

    const days = Math.floor(totalHours / 24);
    const daysString = days > 1 ? 'Days' : 'Day';
    const hoursString = hours > 1 ? 'Hours' : 'Hour';
    const minutesString = minutes > 1 ? 'Minutes' : 'Minute';

    return `${days} ${daysString} ${hours} ${hoursString} ${minutes} ${minutesString}`;
  });

  watch(startDateModel, () => setLengthOfHold());
  watch(endDateModel, () => setLengthOfHold());
  watch(holdReasonModel, () => setDatesFromHoldReason());
  watch(holdStatusModel, () => setHoldStatusField());

  function setForm(sourceData?: any, isSimplifiedEdit: boolean = false) {
    isSimplifiedEditMode.value = isSimplifiedEdit;
    const updatedFieldSchema = getDetailsSchema(rawFields, fieldsSettings);

    if (isSimplifiedEdit) {
      updatedFieldSchema.forEach((field) => {
        if (field.name === 'hold_comment') return;

        field.settings.show = false;
        field.settings.required = false;
        field.state.errors.clear();
      });
    }

    if (!sourceData) return (fieldsSchema.value = updatedFieldSchema);

    formError.value = null;
    wasFormSubmitted.value = false;
    isInitialFormSetup.value = true;

    updatedFieldSchema.forEach((field) => {
      const match = sourceData[field.name];
      switch (field.name) {
        case 'hold_status': {
          field.model = sourceData.custom_hold ? 'custom' : 'holdStatus';
          field.value = sourceData.custom_hold ? 'custom' : 'holdStatus';
          break;
        }
        case 'hold_reason': {
          const matchReason = pageData.value?.holds?.values.find(
            (option: any) => option?._id?.$oid === sourceData?.hold_settings_id?.$oid
          );

          field.model = sourceData.custom_hold ? null : matchReason;
          field.value = sourceData.custom_hold ? null : matchReason;
          field.settings.required = !isSimplifiedEdit ? !sourceData.custom_hold : false;
          field.settings.show = !isSimplifiedEdit ? !sourceData.custom_hold : false;
          lastHoldReasonValue.value = sourceData.custom_hold ? null : matchReason;
          break;
        }
        case 'custom_hold_reason': {
          field.model = sourceData.custom_hold ? sourceData.label : null;
          field.value = sourceData.custom_hold ? sourceData.label : null;
          field.settings.required = !isSimplifiedEdit ? sourceData.custom_hold : false;
          field.settings.show = !isSimplifiedEdit ? sourceData.custom_hold : false;
          break;
        }
        case 'start_date': {
          field.model = new Date(sourceData.start_date);
          field.value = new Date(sourceData.start_date);
          break;
        }
        case 'end_date': {
          field.model = new Date(sourceData.end_date);
          field.value = new Date(sourceData.end_date);
          break;
        }
        case 'hold_comment': {
          field.model = sourceData.comment ?? '';
          field.value = sourceData.comment ?? '';
          break;
        }
        default: {
          setMatchField(field, match);
        }
      }
    });

    fieldsSchema.value = updatedFieldSchema;
    isInitialFormSetup.value = false;
  }

  function setHoldStatusField() {
    if (isSimplifiedEditMode.value) return;

    fieldsSchema.value.forEach((field) => {
      const showField = holdStatusModel.value === 'custom';

      if (field.name === 'hold_status') {
        field.model = showField ? 'custom' : 'holdStatus';
        field.value = showField ? 'custom' : 'holdStatus';
      }

      if (field.name === 'hold_reason') {
        field.settings.required = !showField;
        field.settings.show = !showField;
        field.state.errors.clear();
      }
      if (field.name === 'custom_hold_reason') {
        field.settings.required = showField;
        field.settings.show = showField;
        field.state.errors.clear();
      }
    });
  }

  function setLengthOfHold() {
    fieldsSchema.value.forEach((field) => {
      if (field.name === 'start_date') {
        const emptyStartDate = startDateModel.value === '';
        if (emptyStartDate) {
          field.model = new Date();
          field.value = new Date();
          return;
        }
      }

      if (field.name === 'end_date') {
        const emptyEndDate = endDateModel.value === '';
        if (emptyEndDate) {
          field.model = new Date();
          field.value = new Date();
        }

        const isStartDateGreaterThanEndDate = startDateModel.value > endDateModel.value;
        if (isStartDateGreaterThanEndDate) {
          field.model = new Date(startDateModel.value);
          field.value = new Date(startDateModel.value);
        }
        field.settings.minDate = startDateModel.value;
      }

      if (field.name === 'length_of_hold') {
        field.value = lengthOfHold.value;
        field.model = lengthOfHold.value;
      }
    });
  }

  function setDatesFromHoldReason() {
    if (isInitialFormSetup.value) return;

    const isUserChange = lastHoldReasonValue.value !== holdReasonModel.value;
    const isCustomHold = holdStatusModel.value === 'custom';
    if (isCustomHold || !isUserChange) return;

    const now = new Date();

    if (startDateField.value) {
      startDateField.value.model = now;
      startDateField.value.value = now;
    }

    if (!holdReasonModel.value) {
      if (endDateField.value) {
        endDateField.value.model = now;
        endDateField.value.value = now;
      }
      return;
    }

    const days = Number(holdReasonModel.value.days);
    const endDateCalculated = new Date(now);
    endDateCalculated.setDate(now.getDate() + days);

    if (endDateField.value) {
      endDateField.value.model = endDateCalculated;
      endDateField.value.value = endDateCalculated;
    }

    lastHoldReasonValue.value = holdReasonModel.value;
  }

  function setMatchField(field: any, match: any) {
    field.model = match;
    field.value = match;
  }

  function resetForm() {
    currentSchemaModels.value = new Set();
    fieldsSchema.value = [];
    formError.value = null;
    isInitialFormSetup.value = false;
    lastHoldReasonValue.value = null;
    isSimplifiedEditMode.value = false;
  }

  return {
    currentSchemaModels,
    fieldsSchema,
    formError,
    holdCommentModel,
    lengthOfHold,
    rawFields,
    resetForm,
    setDatesFromHoldReason,
    setForm,
  };
}
